import { useCiTradeWebSocketStore } from '@/app/hooks/ciTradeWebsocketStore';
import useTradeStore from '@/app/hooks/trade/tradeStore';
import { throttle } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  ResponsiveContainer,
  ReferenceLine,
  Tooltip,
} from 'recharts';

const DepthContainer = () => {
  const { data: tradeStore } = useTradeStore();
  const depthAsks = useCiTradeWebSocketStore((s) => s.depthAsks);
  const depthBids = useCiTradeWebSocketStore((s) => s.depthBids);

  const [throttledLong, setThrottledLong] = useState<any[]>([]);
  const [throttledShort, setThrottledShort] = useState<any[]>([]);

  const formattedOrderBook = useMemo(() => {
    if (!depthAsks || !depthBids || !tradeStore?.pairs) return null;

    const rawSymbol = tradeStore?.pairs.replace('/', '').toLowerCase();
    const isPepe = rawSymbol === 'pepeusdt';

    const formatSide = (side: any[]) => {
      let runningSum = 0;

      const formatted = side.map(
        ([priceStr, quantityStr]: [string, string]) => {
          let price = parseFloat(priceStr);
          let quantity = parseFloat(quantityStr);

          if (isPepe) {
            price *= 1000;
            quantity /= 1e7;
          }

          const sum = price * quantity;
          runningSum += sum;

          return {
            price,
            amount: quantity,
            sumAmount: runningSum,
          };
        }
      );

      return { formatted };
    };

    const bids = formatSide(depthBids);
    const asks = formatSide([...depthAsks].reverse());

    return {
      long: bids.formatted,
      short: asks.formatted,
    };
  }, [depthAsks, depthBids, tradeStore?.pairs]);

  const throttledUpdate = useMemo(
    () =>
      throttle((newData: any) => {
        setThrottledLong(newData?.long);
        setThrottledShort(newData?.short);
      }, 3000),
    []
  );

  useEffect(() => {
    if (formattedOrderBook) throttledUpdate(formattedOrderBook);
  }, [formattedOrderBook]);

  // 차트 데이터 준비
  const chartData = useMemo(() => {
    if (!throttledLong?.length || !throttledShort?.length) return [];

    // bids 데이터 처리 - 가격이 높은 순서부터 누적
    const sortedBids = [...throttledLong].sort((a, b) => b.price - a.price);
    let bidCumulative = 0;
    const processedBids = sortedBids.map((item) => {
      bidCumulative += item.amount;
      return {
        price: item.price,
        bidAmount: bidCumulative,
        askAmount: null, // null로 설정하여 해당 영역에서 렌더링하지 않음
        type: 'bid',
      };
    });

    // asks 데이터 처리 - 가격이 낮은 순서부터 누적
    const sortedAsks = [...throttledShort].sort((a, b) => a.price - b.price);
    let askCumulative = 0;
    const processedAsks = sortedAsks.map((item) => {
      askCumulative += item.amount;
      return {
        price: item.price,
        bidAmount: null, // null로 설정하여 해당 영역에서 렌더링하지 않음
        askAmount: askCumulative,
        type: 'ask',
      };
    });

    // 모든 데이터를 합치고 가격순으로 정렬
    return [...processedBids, ...processedAsks].sort(
      (a, b) => a.price - b.price
    );
  }, [throttledLong, throttledShort]);

  // 현재 가격 계산
  const currentPrice = useMemo(() => {
    if (!throttledLong?.length || !throttledShort?.length) return 0;
    const highestBid = Math.max(...throttledLong.map((item) => item.price));
    const lowestAsk = Math.min(...throttledShort.map((item) => item.price));
    return (highestBid + lowestAsk) / 2;
  }, [throttledLong, throttledShort]);

  // 커스텀 툴팁
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const hasBid = data.bidAmount !== null;
      const hasAsk = data.askAmount !== null;

      return (
        <div
          style={{
            background: 'rgba(0, 0, 0, 0.8)',
            border: '1px solid #ccc',
            borderRadius: '4px',
            padding: '8px',
            color: 'white',
          }}
        >
          <p>{`가격: ${Number(label).toLocaleString()}`}</p>
          {hasBid && (
            <p style={{ color: '#37A16A' }}>
              {`매수량: ${Number(data.bidAmount).toLocaleString()}`}
            </p>
          )}
          {hasAsk && (
            <p style={{ color: '#F71053' }}>
              {`매도량: ${Number(data.askAmount).toLocaleString()}`}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  console.log('chartData:', chartData); // 디버깅용
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        background: '#1a1a1a',
        paddingRight: '10px',
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={chartData}
          margin={{ top: 10, right: 30, left: 5, bottom: 5 }}
        >
          <defs>
            <linearGradient id="bidGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#37A16A" stopOpacity={0.6} />
              <stop offset="100%" stopColor="#37A16A" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="askGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#F71053" stopOpacity={0.6} />
              <stop offset="100%" stopColor="#F71053" stopOpacity={0} />
            </linearGradient>
          </defs>

          <XAxis
            dataKey="price"
            type="number"
            scale="linear"
            domain={['dataMin', 'dataMax']}
            tickFormatter={(value) => Number(value).toLocaleString()}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: '#888' }}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: '#888' }}
            tickFormatter={(value) => Number(value).toLocaleString()}
          />
          <Tooltip content={<CustomTooltip />} />

          {/* 현재 가격 기준선 */}
          <ReferenceLine
            x={currentPrice}
            stroke="#FFD700"
            strokeWidth={2}
            strokeDasharray="5 5"
          />

          {/* 매수 (Bids) 영역 - 초록색, null 값에서는 렌더링 안함 */}
          <Area
            dataKey="bidAmount"
            stroke="#37A16A"
            fill="url(#bidGradient)"
            strokeWidth={2}
            type="stepAfter"
            connectNulls={false}
          />

          {/* 매도 (Asks) 영역 - 빨간색, null 값에서는 렌더링 안함 */}
          <Area
            dataKey="askAmount"
            stroke="#F71053"
            fill="url(#askGradient)"
            strokeWidth={2}
            type="stepAfter"
            connectNulls={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default DepthContainer;
